import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, ArrowRight, Mail } from "lucide-react";
import { getSituation, SITUATIONS } from "@/data/situations";
import { FIRM, TEAM } from "@/data/content";
import { PageHero, Breadcrumbs, FadeIn } from "@/components/Shared";

export default function Situation() {
  const { slug } = useParams();
  const situation = getSituation(slug);
  if (!situation) return <Navigate to="/how-we-help" replace />;

  const namita = TEAM[0];
  const others = SITUATIONS.filter((s) => s.slug !== slug).slice(0, 4);

  return (
    <main data-testid="situation-page">
      <PageHero eyebrow="How we help" title={situation.title} lead={situation.lead}>
        {situation.urgent && (
          <a
            href={`tel:${FIRM.emergencyPhone.replace(/\s/g, "")}`}
            className="brand-cta-primary mt-8 inline-flex items-center gap-2 px-7 py-3.5 tracking-[0.2em]"
            data-testid="situation-urgent-call"
          >
            <Phone className="size-4" /> Call now — 24hr: {FIRM.emergencyPhone}
          </a>
        )}
      </PageHero>
      <Breadcrumbs items={[{ label: "How We Help", href: "/how-we-help" }, { label: situation.title }]} />

      <section className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="brand-container">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <FadeIn>
                <p className="text-lg font-light leading-relaxed text-slate-600">{situation.body}</p>
              </FadeIn>

              <FadeIn delay={0.1}>
                <h2 className="brand-heading mt-12 text-2xl sm:text-3xl">
                  What to do now
                </h2>
                <ol className="mt-6 space-y-5" data-testid="situation-steps">
                  {situation.steps.map((step, i) => (
                    <li key={i} className="flex gap-5 border border-slate-200 bg-brand-ivory p-6">
                      <span className="flex size-9 shrink-0 items-center justify-center bg-brand-navy text-sm font-semibold text-brand-gold">
                        {i + 1}
                      </span>
                      <div>
                        <h3 className="font-medium text-brand-navy">{step.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{step.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </FadeIn>

              {situation.related?.length > 0 && (
                <FadeIn delay={0.15}>
                  <div className="brand-panel-pale mt-12 border-l-2 border-brand-gold p-8">
                    <p className="brand-eyebrow tracking-[0.22em]">
                      Detailed guidance for your case
                    </p>
                    <ul className="mt-4 space-y-2">
                      {situation.related.map((r) => (
                        <li key={r.href}>
                          <Link
                            to={r.href}
                            className="group inline-flex items-center gap-2 text-base font-medium text-brand-navy transition-colors hover:text-brand-terracotta"
                            data-testid={`situation-related-${r.href.split("/").pop()}`}
                          >
                            {r.label}
                            <ArrowRight className="size-4 text-brand-gold transition-transform duration-200 group-hover:translate-x-1" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </FadeIn>
              )}
            </div>

            <aside className="lg:col-span-4">
              <FadeIn delay={0.1}>
                <div className="brand-panel-dark p-8" data-testid="situation-contact-card">
                  <p className="text-xl font-light">Speak to us in confidence.</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-300">
                    Discreet, empathetic and answered 24 hours a day. We act for both
                    privately and publicly funded clients.
                  </p>
                  <div className="mt-5 space-y-2.5 text-sm">
                    <a href={`tel:${FIRM.emergencyPhone.replace(/\s/g, "")}`} className="brand-link-on-dark flex items-center gap-2.5" data-testid="situation-contact-emergency-phone">
                      <Phone className="size-4 text-brand-terracotta" /> 24hr: {FIRM.emergencyPhone}
                    </a>
                    <a href={`tel:${FIRM.phone.replace(/\s/g, "")}`} className="brand-link-on-dark flex items-center gap-2.5" data-testid="situation-contact-office-phone">
                      <Phone className="size-4 text-brand-terracotta" /> Office: {FIRM.phone}
                    </a>
                    <a href={`mailto:${FIRM.email}`} className="brand-link-on-dark flex items-center gap-2.5" data-testid="situation-contact-email">
                      <Mail className="size-4 text-brand-terracotta" /> {FIRM.email}
                    </a>
                  </div>
                  <Link
                    to="/contact"
                    className="brand-cta-primary mt-6 block py-3 text-center"
                    data-testid="situation-contact-cta"
                  >
                    Make an enquiry
                  </Link>
                </div>

                <div className="mt-6 border border-slate-200 p-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                    Other situations
                  </p>
                  <ul className="mt-4 space-y-3">
                    {others.map((o) => (
                      <li key={o.slug}>
                        <Link
                          to={`/how-we-help/${o.slug}`}
                          className="text-sm font-medium text-slate-700 transition-colors hover:text-brand-terracotta"
                          data-testid={`other-situation-${o.slug}`}
                        >
                          {o.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="brand-panel-pale mt-6 border border-slate-200 p-6">
                  <p className="text-sm font-medium text-brand-navy">{namita.name}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-500">{namita.role}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    "{namita.short}"
                  </p>
                </div>
              </FadeIn>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}
